import { type ReactElement } from 'react';
import { NavLink, Navigate, Outlet, useParams } from 'react-router';
import { useSession } from './ContexteSession.js';
import { CHEMINS, entreesVisibles } from '../routes.js';

/**
 * LA COQUILLE D'UN ESPACE — l'entete, la navigation, et la place du contenu.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ ELLE RESTE EN PLACE QUAND LE CONTENU CHANGE.                          ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * C'est ce qui distingue une application d'un site : on ne recharge pas
 * l'entete pour passer des automatisations aux executions. `<Outlet />` marque
 * l'emplacement ou les ecrans viennent se loger -- l'editeur graphique de
 * l'etape 22 s'y branchera sans rien deplacer.
 *
 * ── UN CODE D'ESPACE INCONNU N'EST PAS UNE ERREUR TECHNIQUE ─────────────
 *
 * L'utilisateur a pu suivre un lien vers un espace dont il a ete retire, ou se
 * tromper en tapant. On le RENVOIE a sa liste plutot que d'afficher une page
 * cassee -- et sans dire si l'espace existe ailleurs, ce que l'API ne dirait
 * pas davantage : elle rend le meme 404 pour un espace inexistant et pour un
 * espace dont on n'est pas membre.
 */
export function Coquille(): ReactElement {
  const { etat, deconnecter } = useSession();
  const { code } = useParams<{ code: string }>();

  if (etat.phase !== 'connecte') return <Navigate to={CHEMINS.connexion} replace />;
  const espace = etat.vue.locataires.find((l) => l.code === code);
  if (espace === undefined) return <Navigate to={CHEMINS.espaces} replace />;

  // MASQUER, JAMAIS AUTORISER. L'API tranche seule ; on evite simplement de
  // proposer une porte qu'on sait fermee.
  const entrees = entreesVisibles(espace.role);

  return (
    <div className="coquille">
      <a href="#contenu" className="saut-contenu">Aller au contenu</a>

      <header className="entete">
        <div className="entete__droite">
          {/* LA MARQUE EST LA PORTE DE SORTIE, TOUJOURS. Elle ne depend ni
              du role ni du nombre d espaces : depuis 29-B, /espaces est le
              seul carrefour vers le monde plateforme [28-BU], et un
              operateur n a qu un espace -- « Changer d espace » restait
              donc masque et l enfermait dans le client. */}
          <NavLink to={CHEMINS.espaces} className="entete__marque"
                   aria-label="Retour a l accueil Anelya">
            Anelya
          </NavLink>
          <span className="discret">{espace.nom}</span>
          <span className="etiquette">{espace.role}</span>
        </div>
        <div className="entete__droite">
          {/* Le changement d'espace n'a de sens qu'a partir de deux. */}
          {etat.vue.locataires.length > 1 && (
            <NavLink to={CHEMINS.espaces} className="bouton bouton--discret">
              Changer d’espace
            </NavLink>
          )}
          <span className="discret">{etat.vue.utilisateur.nom}</span>
          <button type="button" className="bouton bouton--discret"
                  onClick={() => { void deconnecter(); }}>
            Se déconnecter
          </button>
        </div>
      </header>

      <nav className="barre" aria-label="Sections de l’espace">
        {entrees.map((e) => (
          e.pret ? (
            <NavLink key={e.cle} to={e.chemin(espace.code)}
                     className={({ isActive }) =>
                       'barre__lien' + (isActive ? ' barre__lien--actif' : '')}>
              {e.libelle}
            </NavLink>
          ) : (
            // UN LIEN GRISE DIT QUE LA RUBRIQUE VIENDRA. Son absence laisserait
            // croire qu'elle n'est pas prevue -- et l'API la sert deja.
            <span key={e.cle} className="barre__lien" aria-disabled="true"
                  title="Bientôt disponible">
              {e.libelle}
            </span>
          )
        ))}
      </nav>

      <main className="contenu" id="contenu">
        <Outlet context={espace} />
      </main>
    </div>
  );
}
